// Timeline events query.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './api';
import { useMe } from './auth';

export type TimelineEvent = {
  id: string;
  case_id: string;
  event_date: string;
  event_type:
    | 'agreement'
    | 'payment'
    | 'work_day'
    | 'missed_deadline'
    | 'defect_discovery'
    | 'complaint_sent'
    | 'contractor_response'
    | 'abandonment'
    | 'inspection'
    | 'other';
  summary: string;
  source: 'chat' | 'manual' | 'whatsapp' | 'document';
  source_excerpt: string | null;
  source_message_id: string | null;
  source_file_id: string | null;
  source_turn_ids: number[] | null;
  source_page_nos: number[] | null;
  /** ISO timestamp of the FIRST cited turn (WhatsApp only). Server-side
   *  lookup from extracted_turns; null when no turn or no ts on the turn. */
  source_turn_ts?: string | null;
  /** How many other events were merged into this one by the cross-file
   *  dedup pass. > 0 means "this fact also appears in N other files". */
  corroborating_count?: number;
  /** The merged-duplicate rows themselves, so the UI can build one
   *  numbered footnote registry without a per-click round trip. */
  corroborating_sources?: Array<{
    id: string;
    source: 'chat' | 'manual' | 'whatsapp' | 'document';
    source_file_id: string | null;
    source_message_id: string | null;
    source_excerpt: string | null;
    source_turn_ids: number[] | null;
    source_page_nos: number[] | null;
    source_turn_ts?: string | null;
  }>;
  created_at: string;
  updated_at: string;
};

export const timelineQueryKey = (caseId: string) => ['cases', caseId, 'timeline'] as const;

export function useTimeline(caseId: string | undefined) {
  const me = useMe();
  return useQuery({
    queryKey: timelineQueryKey(caseId ?? ''),
    queryFn: () => apiFetch<{ events: TimelineEvent[] }>(`/cases/${caseId}/timeline`),
    select: (d) => d.events,
    enabled: !!caseId && !!me.data?.user,
  });
}

/** Cross-source open questions the dedup LLM flagged for human review.
 *  Reframed in MVP item 1: these aren't "conflicts" the homeowner has
 *  to adjudicate — they're things a lawyer would later ask about, and
 *  the homeowner can answer them, dismiss them, or tell the assistant
 *  to look into them. */
export type TimelineInconsistency = {
  id: string;
  case_id: string;
  kind: 'amount' | 'date' | 'party' | 'status' | 'other';
  description: string;
  event_ids: string[];
  prompt_version: string;
  resolved_at: string | null;
  resolution_note: string | null;
  /** Free-text answer the homeowner typed. Distinct from
   *  `resolution_note` (which only fills when `resolved_at` is set).
   *  When this is non-null but `resolved_at` is null, the row stays
   *  visible in the banner — the homeowner has provided context, not
   *  dismissed the question. */
  homeowner_note: string | null;
  created_at: string;
  updated_at: string;
};

export const inconsistenciesQueryKey = (caseId: string) =>
  ['cases', caseId, 'timeline', 'inconsistencies'] as const;

/** Pending (unresolved) inconsistencies for this case. The UI renders
 *  these as a banner above the timeline until each one is resolved. */
export function useInconsistencies(caseId: string | undefined) {
  const me = useMe();
  return useQuery({
    queryKey: inconsistenciesQueryKey(caseId ?? ''),
    queryFn: () =>
      apiFetch<{ inconsistencies: TimelineInconsistency[] }>(
        `/cases/${caseId}/timeline/inconsistencies`,
      ),
    select: (d) => d.inconsistencies,
    enabled: !!caseId && !!me.data?.user,
  });
}

/** Mark an inconsistency resolved with an optional note. The row stays
 *  in the table for audit (rule 7a) — it just disappears from the
 *  pending list. */
export function useResolveInconsistency(caseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note: string | null }) =>
      apiFetch<{ ok: true }>(`/cases/${caseId}/timeline/inconsistencies/${id}/resolve`, {
        method: 'POST',
        body: { note },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: inconsistenciesQueryKey(caseId) }),
  });
}

/** Save the homeowner's answer to a flagged question without resolving
 *  the row. The card stays visible — the user has answered, not
 *  dismissed. Used by the "Add answer" action in the banner. Pass
 *  `note: null` to clear an existing answer. */
export function useSetHomeownerNote(caseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note: string | null }) =>
      apiFetch<{ ok: true }>(`/cases/${caseId}/timeline/inconsistencies/${id}/note`, {
        method: 'POST',
        body: { note },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: inconsistenciesQueryKey(caseId) }),
  });
}

/** Trigger a timeline refetch — call this from the chat's onFinish hook. */
export function useInvalidateTimeline() {
  const qc = useQueryClient();
  return (caseId: string) => qc.invalidateQueries({ queryKey: timelineQueryKey(caseId) });
}

export function useDeleteTimelineEvent(caseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (eventId: string) =>
      apiFetch<{ ok: true }>(`/cases/${caseId}/timeline/${eventId}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: timelineQueryKey(caseId) }),
  });
}
